import React, { useState } from 'react';
import Card from '@/components/card';
import Title from '@/components/title';
import { settingMenu } from '@/configs/menuItems';
import SubMenu from '@/components/menu/sub-menu';
import {
  Button,
  Dropdown,
  Input,
  MenuProps,
  TablePaginationConfig,
} from 'antd';
import UserTable from './components/user-table';
import { ISearchUserReq, ISearchUserRes, IUser } from '@/interfaces/user';
import { IDataModal } from '@/interfaces/dataModal';
import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { searchUsers } from '@/adapters/user';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { PlusOutlined, FilterOutlined } from '@ant-design/icons';
import UserAddModal from './components/user-add-modal';
import UserResetPasswordModal from './components/user-reset-password-modal';
import BusinessUnitSelector from '@/components/selector/business-unit-selector';
import { getPathSegment } from '@/utils/pathUtils';
import { useAuth } from '@/contexts/AuthContext';

const { Search } = Input;

const _criteria: ISearchUserReq = {
  orderBy: 'id',
  orderType: 'descend',
  pageSize: 10,
  pageStart: 1,
  search: '',
};

const _userModal: IDataModal<IUser> = {
  open: false,
};

const UserSetting = () => {
  const pathSegment = getPathSegment();
  const { userInfo } = useAuth();
  const [criteria, setCriteria] = useState<ISearchUserReq>({
    ..._criteria,
    businessUnitId: userInfo?.bu || undefined,
  });

  const [dataModal, setDataModal] = useState<IDataModal<IUser>>(_userModal);
  const [dataResetModal, setDataResetModal] =
    useState<IDataModal<IUser>>(_userModal);

  const { data, isLoading, refetch, isFetching } = useQuery<
    AxiosResponse<ISearchUserRes>,
    Error
  >({
    queryKey: ['search-user', criteria],
    queryFn: async () => await searchUsers(criteria),
  });

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IUser> | SorterResult<IUser>[],
  ) => {
    const newCriteria = { ...criteria };
    if (!Array.isArray(sorter)) {
      newCriteria.orderBy = (sorter.column?.dataIndex as keyof IUser) || '';
      newCriteria.orderType = sorter.order || '';
    }
    setCriteria({
      ...newCriteria,
      pageStart: Number(pagination.current || 1),
      pageSize: Number(pagination.pageSize || 10),
    });
  };

  const onSearch = (text: string) => {
    setCriteria({
      ...criteria,
      search: text,
      orderBy: 'id',
      orderType: '',
      pageStart: 1,
      pageSize: 10,
    });
  };

  const handleMenuClick: MenuProps['onClick'] = e => {
    switch (e.key) {
      case '1':
        setCriteria({
          ...criteria,
          isActive: undefined,
          isDelete: undefined,
        });
        break;
      case '2':
        setCriteria({
          ...criteria,
          isActive: true,
          isDelete: undefined,
        });
        break;
      case '3':
        setCriteria({
          ...criteria,
          isActive: false,
          isDelete: undefined,
        });
        break;
      case '4':
        setCriteria({
          ...criteria,
          isActive: undefined,
          isDelete: true,
        });
        break;
      default:
        break;
    }
  };

  const items: MenuProps['items'] = [
    {
      label: 'ทั้งหมด',
      key: '1',
    },
    {
      label: 'ใช้งาน',
      key: '2',
    },
    {
      label: 'ไม่ใข้งาน',
      key: '3',
    },
    {
      label: 'ถังขยะ',
      key: '4',
    },
  ];

  const menuProps = {
    items,
    onClick: handleMenuClick,
  };

  return (
    <>
      <SubMenu active={pathSegment} items={settingMenu} />
      <Card>
        <Title title="จัดการผู้ใช้งาน">
          <Button
            type="primary"
            className="max-w-[90px]"
            onClick={() => {
              setDataModal({
                ...dataModal,
                open: true,
              });
            }}
          >
            <PlusOutlined /> เพิ่มใหม่
          </Button>
        </Title>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col md:flex-row justify-between gap-3">
            <div className="flex flex-col md:flex-row justify-between gap-3">
              <Search
                className="md:max-w-[400px] md:min-w-[400px]"
                placeholder="ค้นหา"
                allowClear
                size="large"
                enterButton="ค้นหา"
                onSearch={onSearch}
              />
              <BusinessUnitSelector
                handleSelectData={id => {
                  setCriteria({
                    ...criteria,
                    businessUnitId: id,
                  });
                }}
                value={criteria.businessUnitId}
                disabled={!!userInfo?.bu}
              />
              <Dropdown.Button
                size="large"
                menu={menuProps}
                placement="bottom"
                icon={<FilterOutlined />}
              >
                สถานะ
              </Dropdown.Button>
            </div>
          </div>
          <UserTable
            criteria={criteria}
            user={data?.data || ({} as ISearchUserRes)}
            loading={isLoading || isFetching}
            onDelete={() => refetch()}
            onTableChange={onTableChange}
            onEdit={user => {
              setDataModal({
                ..._userModal,
                open: true,
                id: user.id,
                item: user,
              });
            }}
            onResetPassword={user => {
              setDataResetModal({
                ..._userModal,
                open: true,
                id: user.id,
                item: user,
              });
            }}
          />
          <UserAddModal
            handleCancel={() =>
              setDataModal({
                ..._userModal,
              })
            }
            handleOk={() => {
              refetch();
              setDataModal({
                ..._userModal,
              });
            }}
            dataModal={dataModal}
          />
          <UserResetPasswordModal
            handleCancel={() =>
              setDataResetModal({
                ..._userModal,
              })
            }
            handleOk={() => {
              refetch();
              setDataResetModal({
                ..._userModal,
              });
            }}
            dataModal={dataResetModal}
          />
        </div>
      </Card>
    </>
  );
};

export default UserSetting;
